import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CatalogService } from "@/gen/omnios/v1/catalog_connect";
import type { CategoryCompleteness } from "@/gen/omnios/v1/catalog_pb";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	AlertCircle,
	BarChart3,
	CheckCircle2,
	Loader2,
	RefreshCw,
} from "lucide-react";

export const Route = createFileRoute("/settings/catalog/completeness")({
	component: CompletenessPage,
});

const client = createClient(CatalogService, transport);

function CompletenessPage() {
	useBreadcrumbs([
		{ label: "Настройки", href: "/settings" },
		{ label: "Каталог", href: "/settings/catalog" },
		{ label: "Заполненность" },
	]);

	const qc = useQueryClient();
	const { data, isLoading, error } = useQuery({
		queryKey: ["catalog-completeness"],
		queryFn: () => client.getCatalogCompleteness({ categoryId: "" }),
	});

	const categories = data?.categories ?? [];
	const overall = data?.overallFillRatePct ?? 0;
	const total = data?.totalProducts ?? 0;

	if (error) {
		return (
			<Card className="flex flex-col items-center py-12 gap-3">
				<AlertCircle className="h-10 w-10 text-destructive" />
				<p className="text-sm text-muted-foreground">
					Не удалось загрузить данные: {error.message}
				</p>
			</Card>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-lg font-semibold">Заполненность каталога</h2>
					<p className="text-sm text-muted-foreground">
						Анализ заполненности обязательных атрибутов по категориям
					</p>
				</div>
				<Button
					variant="outline"
					size="sm"
					onClick={() =>
						qc.invalidateQueries({ queryKey: ["catalog-completeness"] })
					}
				>
					<RefreshCw className="h-4 w-4 mr-1" />
					Обновить
				</Button>
			</div>

			{isLoading && (
				<div className="flex justify-center py-12">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			)}

			{!isLoading && total > 0 && (
				<div className="grid grid-cols-3 gap-4">
					<Card className="px-4 py-3">
						<div className="text-2xl font-bold tabular-nums">{total}</div>
						<div className="text-sm text-muted-foreground">Товаров</div>
					</Card>
					<Card className="px-4 py-3">
						<div className="text-2xl font-bold tabular-nums">
							{overall.toFixed(1)}%
						</div>
						<div className="text-sm text-muted-foreground">
							Общая заполненность
						</div>
					</Card>
					<Card className="px-4 py-3">
						<div className="text-2xl font-bold tabular-nums">
							{categories.length}
						</div>
						<div className="text-sm text-muted-foreground">Категорий</div>
					</Card>
				</div>
			)}

			{!isLoading && categories.length === 0 && (
				<Card className="flex flex-col items-center py-12 gap-3">
					<BarChart3 className="h-10 w-10 text-muted-foreground" />
					<p className="text-sm text-muted-foreground">
						Нет категорий с обязательными свойствами
					</p>
				</Card>
			)}

			{!isLoading && (
				<div className="space-y-3">
					{categories.map((cat) => (
						<CategoryCard key={cat.categoryId} cat={cat} />
					))}
				</div>
			)}
		</div>
	);
}

function fillColor(pct: number): string {
	if (pct >= 80) return "bg-green-500";
	if (pct >= 50) return "bg-yellow-500";
	return "bg-red-500";
}

function fillBorderColor(pct: number): string {
	if (pct >= 80) return "hsl(var(--chart-2))";
	if (pct >= 50) return "#eab308";
	return "hsl(var(--destructive))";
}

function CategoryCard({ cat }: { cat: CategoryCompleteness }) {
	const pct = cat.fillRatePct;

	return (
		<Card className="px-4 py-3 space-y-3">
			<div className="flex items-center justify-between">
				<div>
					<span className="text-sm font-medium">{cat.categoryName}</span>
					{cat.categoryPath && (
						<span className="text-xs text-muted-foreground ml-2">
							{cat.categoryPath}
						</span>
					)}
				</div>
				<div className="flex items-center gap-2">
					<Badge variant="secondary">{cat.totalProducts} товаров</Badge>
					<span className="text-sm font-medium tabular-nums">
						{pct.toFixed(0)}%
					</span>
				</div>
			</div>

			<div className="h-2 bg-muted rounded-full overflow-hidden">
				<div
					className={`h-full rounded-full transition-all ${fillColor(pct)}`}
					style={{ width: `${Math.min(pct, 100)}%` }}
				/>
			</div>

			{cat.fields.length > 0 && (
				<div className="space-y-1">
					{cat.fields.map((f) => (
						<div
							key={f.propertySlug}
							className="flex items-center justify-between text-xs"
						>
							<div className="flex items-center gap-1.5">
								{f.fillRatePct >= 100 ? (
									<CheckCircle2 className="h-3 w-3 text-green-500" />
								) : (
									<div
										className="h-3 w-3 rounded-full border-2"
										style={{
											borderColor: fillBorderColor(f.fillRatePct),
										}}
									/>
								)}
								<span>{f.propertyName}</span>
								{f.isRequired && <span className="text-destructive">*</span>}
							</div>
							<span className="text-muted-foreground tabular-nums">
								{f.filledCount}/{f.totalCount}
							</span>
						</div>
					))}
				</div>
			)}
		</Card>
	);
}
